import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    ListPublishedNews200Response,
} from '@viewbid/ts-angular-client';
import { VbNewsArticle } from '@viewbid/ui';
import { VbSnackbarService } from '@viewbid/ui';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProductService } from '@/shared/services/product.service';
import { DeviceCheckService } from '../../services/device-check.service';
import { mapNewsListItem } from '../../utils/news-ui-mapper.util';

@Component({
    selector: 'vb-news-section-page',
    templateUrl: './news-section-page.component.html',
    styleUrls: ['./news-section-page.component.scss'],
})
export class NewsSectionPageComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    public isMobile: boolean = false;

    constructor(
        private auctionCatalogService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private translateService: TranslateService,
        private deviceCheckService: DeviceCheckService,
        private productService: ProductService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}
    public articles: VbNewsArticle[] = [];
    public paginate: any = {}; // Pagination use only
    public pageNo: number = 1;
    public pageSize: number = 10;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    public isLoading: boolean = false;
    isNoData: boolean = false;
    isFailed: boolean = false;

    ngOnInit(): void {
        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });

        this.pageNo = Number(this.route.snapshot.queryParams['page']) || 1;
        this.fetchNewsArticles();

        // React to URL page changes (e.g. browser back/forward) — the single
        // loader for pagination, guarded so paginator clicks can't re-trigger it.
        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe((params: Params) => {
                const page: number = Number(params['page']) || 1;
                if (page !== this.pageNo) {
                    this.pageNo = page;
                    this.fetchNewsArticles();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Opens the article route. No state rides along — a back press restores
     * this index (page included) through browser history.
     */
    openArticle(article: VbNewsArticle): void {
        this.router.navigate(['/news', article.id]);
    }

    setPage(page: number): void {
        // Drive the URL only; the queryParams subscription is the single loader
        // so browser back/forward and paginator clicks share one path.
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { page },
            queryParamsHandling: 'merge',
        });
    }

    setPageSize(pageSize: number): void {
        this.pageSize = pageSize;
        this.pageNo = 1;
        // Page size isn't tracked in the URL, so reload directly. Reset the URL
        // to page 1 too (guard makes the subscription a no-op since pageNo=1).
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { page: 1 },
            queryParamsHandling: 'merge',
        });
        this.fetchNewsArticles();
    }

    private fetchNewsArticles(): void {
        this.isLoading = true;
        this.isFailed = false;
        this.auctionCatalogService
            .listPublishedNews(this.pageNo, this.pageSize)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: ListPublishedNews200Response) => {
                    this.articles = response.data.map(mapNewsListItem);
                    this.paginate = this.productService.getPager(
                        response.totalCount ?? 0,
                        Number(response.page ?? this.pageNo),
                        response.pageSize ?? this.pageSize,
                        false,
                    );
                    this.isNoData = this.articles.length === 0;
                    this.isLoading = false;
                },
                error: () => {
                    this.snackbarService.error(
                        this.translateService.instant(
                            'newsArticle.errorFetchingArticles',
                        ),
                    );
                    this.isNoData = true;
                    this.isFailed = true;
                    this.isLoading = false;
                },
            });
    }
}
